// RFC 9457 problem+json as the control plane emits it (control-plane spec §7.1 "Errors").
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  request_id?: string;
  errors?: { field: string; detail: string }[];
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | null;
  readonly requestId: string | null;
  /** seconds, from Retry-After on 429 */
  readonly retryAfter: number | null;

  constructor(status: number, problem: Problem | null, headers?: Headers) {
    super(problem?.title ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.requestId = problem?.request_id ?? headers?.get('x-request-id') ?? null;
    const ra = headers?.get('retry-after');
    this.retryAfter = ra && /^\d+$/.test(ra) ? Number(ra) : null;
  }

  /** `feature_disabled` is how CDN-only tenants are told a platform route is off (spec §7.1). */
  get isFeatureDisabled(): boolean {
    return this.status === 403 && /feature_disabled/.test(this.problem?.type ?? '');
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

export async function toApiError(res: Response): Promise<ApiError> {
  let problem: Problem | null = null;
  const ct = res.headers.get('content-type') ?? '';
  if (ct.includes('json')) {
    try {
      problem = (await res.json()) as Problem;
    } catch {
      problem = null;
    }
  }
  return new ApiError(res.status, problem, res.headers);
}
